import { gql } from "@apollo/client";
import { useQuery } from "@apollo/client/react";
import { Link, useNavigate } from "react-router";
import styled, { css } from "styled-components";

import CategoryTree from "./CategoryTree";
import ListCategories from "./ListCategories.graphql";
import tree from "./tree";
import * as UI from "../../../../../modules/ui-kit";
import { confirmUnsavedChanges } from "../../../modules/unsaved-changes";

/** Hidden from the app until published — muted rather than struck through. */
const CssNonPublic = css`
    color: #999;
    font-style: italic;
`;

const CssSelected = css`
    font-weight: 600;

    &::before {
        width: calc(100% + .2em);
    }
`;

const StyledCategory = styled(Link)<{ $public?: boolean, $selected?: boolean }>`
    align-items: center;
    color: #000;
    display: flex;
    flex-direction: row;
    gap: 8px;
    overflow: hidden;
    padding: 4px 0;
    position: relative;
    text-decoration: none;
    text-overflow: ellipsis;
    white-space: nowrap;

    ${ UI.FluidAccent2 }

    img {
        flex: none;
        height: 22px;
        opacity: .8;
        width: 22px;
    }

    span {
        overflow: hidden;
        text-overflow: ellipsis;
    }

    ${ ({ $public }) => $public ? undefined : CssNonPublic }
    ${ ({ $selected }) => $selected && CssSelected }
`;

const StyledCategoryList = styled.div`
    --color-fluid: #ffe066;

    font-size: 1.05rem;

    ul {
        display: flex;
        flex-direction: column;
        gap: 3px;
        list-style: none;
        margin: 0;
        padding: 0;
    }

    /* nested levels get an indent and a guide line */
    ul ul {
        border-left: 1px solid #0001;
        margin-left: 10px;
        padding-left: 12px;
    }
`;

export default function CategoryList(props) {
    const { guard } = props;

    const navigate = useNavigate();

    const listCategories = useQuery(gql(ListCategories));

    if (listCategories.loading) return "loading";
    if (listCategories.error) return "error";

    async function open(event, categoryID) {
        if (!guard) return; // no guard supplied — default Link behaviour
        event.preventDefault();
        if (await confirmUnsavedChanges(guard)) navigate(categoryID);
    }

    return (
        <StyledCategoryList>
            <CategoryTree
                render={
                    (category) => (
                        <StyledCategory
                            $public={ category.public }
                            $selected={ category.id === props.selected }
                            onClick={ (e) => open(e, category.id) }
                            title={ category.name[0].text }
                            to={ category.id }
                        >
                            { category.icon && <img alt="" src={ category.icon } /> }
                            <span>{ category.name[0].text }</span>
                        </StyledCategory>
                    )
                }
            >
                { tree(listCategories.data.categories) }
            </CategoryTree>
        </StyledCategoryList>
    );
}
